// Windsurf generated file
import { useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
  Chip,
  Stack,
  CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useFeatureFlags } from '../shared/use-feature-flags'
import { DevOnly } from './dev-only'
import './feature-flag-debug-panel.scss'

// Collapsed button component
const CollapsedButton = ({ onToggle }: { onToggle: () => void }) => (
  <Button
    onClick={onToggle}
    onKeyDown={(e) => e.key === 'Enter' && onToggle()}
    className="feature-flag-debug-panel__toggle-button"
    variant="contained"
    title="Feature Flags Debug Panel"
    aria-label="Open Feature Flags Debug Panel"
  >
    🚩
  </Button>
)

// Expanded panel component
interface ExpandedPanelProps {
  onClose: () => void
}

const ExpandedPanel = ({ onClose }: ExpandedPanelProps) => {
  const {
    flags,
    loading,
    localOverrides,
    toggleLocalOverride,
    toggleAllLocalOverrides,
    clearLocalOverrides,
    pollFlag,
  } = useFeatureFlags()
  const [pollingFlag, setPollingFlag] = useState<string | null>(null)

  const handlePollFlag = async (flagName: keyof typeof flags) => {
    setPollingFlag(flagName)
    await pollFlag(flagName)
    setPollingFlag(null)
  }

  return (
    <Paper className="feature-flag-debug-panel__panel" elevation={3}>
      <Box className="feature-flag-debug-panel__header">
        <Typography variant="body2" component="span">
          🚩 Feature Flags Debug Panel
        </Typography>
        <IconButton
          onClick={onClose}
          className="feature-flag-debug-panel__close-button"
          size="small"
          title="Collapse"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Stack direction="row" spacing={1} sx={{ px: 2, py: 1 }}>
        <Typography variant="body2">
          {loading ? '⏳ Loading...' : '✅ Loaded'}
        </Typography>
        <Button
          onClick={toggleAllLocalOverrides}
          variant="outlined"
          size="small"
          color="primary"
        >
          Toggle All
        </Button>
        {localOverrides.size > 0 && (
          <Button
            onClick={clearLocalOverrides}
            variant="outlined"
            size="small"
            color="error"
          >
            Clear Overrides ({localOverrides.size})
          </Button>
        )}
      </Stack>

      <Box className="feature-flag-debug-panel__flags-section">
        <Typography variant="body2" component="strong" sx={{ px: 2 }}>
          Feature Flags:
        </Typography>
        <Stack spacing={1} sx={{ mt: 1, px: 2, pb: 2 }}>
          {Object.entries(flags)
            .filter(([key]) => key !== 'ENABLE_DEV_TOOLS')
            .map(([key, enabled]) => {
              const hasOverride = localOverrides.has(key)
              const isPolling = pollingFlag === key
              return (
                <Box
                  key={key}
                  className="feature-flag-debug-panel__flag-item"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <Chip
                    label={`${key}: ${enabled ? 'ON' : 'OFF'}${hasOverride ? ' 🔧' : ''}`}
                    color={enabled ? 'success' : 'error'}
                    size="small"
                    variant={hasOverride ? 'filled' : 'outlined'}
                    onClick={() =>
                      toggleLocalOverride(key as keyof typeof flags)
                    }
                    className={`feature-flag-debug-panel__flag-item-status--${
                      enabled ? 'enabled' : 'disabled'
                    }`}
                    sx={{
                      flex: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                  />
                  <IconButton
                    onClick={() =>
                      void handlePollFlag(key as keyof typeof flags)
                    }
                    size="small"
                    color="primary"
                    disabled={isPolling}
                    title={`Poll ${key} from server`}
                    sx={{
                      border: '1px solid',
                      borderColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                      },
                    }}
                  >
                    {isPolling ? (
                      <CircularProgress size={16} />
                    ) : (
                      <RefreshIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              )
            })}
        </Stack>
      </Box>

      <Box sx={{ px: 2, pb: 1, pt: 0 }}>
        <Typography variant="caption" color="text.secondary" component="div">
          Click flag name to toggle override • Toggle All = Toggle all flags •
          🔧 = Override active • 🔄 = Poll server
        </Typography>
      </Box>
    </Paper>
  )
}

export const FeatureFlagDebugPanel = () => {
  const [isExpanded, setIsExpanded] = useState(false)

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <DevOnly>
      {isExpanded ? (
        <ExpandedPanel onClose={toggleExpanded} />
      ) : (
        <CollapsedButton onToggle={toggleExpanded} />
      )}
    </DevOnly>
  )
}
